import { IconContainer } from "@/components/campaign/basic/IconContainer";
import { Card } from "@/components/Card";
import { createTypedTable } from "@/components/generic/Table";
import { Focusable, Tooltip } from "@/components/generic/Tooltip";
import { MainWindow, PageContent } from "@/components/layout/MainWindow";
import { PageHeader } from "@/components/layout/PageHeader";
import { StickyElement } from "@/components/layout/StickyElement";
import { useHorizontalScrollSync } from "@/hooks/use-scroll-sync";
import type { DateValue, Selection } from "react-aria-components";

import { createFileRoute } from "@tanstack/react-router";
import { motion } from "framer-motion";
import {
  Calendar1,
  ChartLine,
  CheckCircle,
  Flame,
  Info,
  UserRoundCheck,
} from "lucide-react";

import { Button as RAButton, SortDescriptor } from "react-aria-components";

import { getCampaignsQueryOptions } from "@/api/campaign.api";

import {
  ActivityStatisticsParams,
  ActivityStatisticsPeriod,
  useActivitiesStatisticsWithRefresh,
} from "@/api/dashboard.api";
import { createUsersQueryOptions } from "@/api/users.api";
import { ActivitiesDashboardGraph } from "@/components/campaign/ActivitiesGraph";
import { CampaignLeadsGraph } from "@/components/campaign/CampaignLeadsGraph";
import { StatusIndicator } from "@/components/campaign/StatusIndicator";
import { DateRangePicker } from "@/components/generic/DateRangePicker";
import { MultiSelect, useMultiSelect } from "@/components/generic/MultiSelect";
import { Separator } from "@/components/generic/Separator";
import { TagGroupBox, TagItem } from "@/components/generic/TagGroup";
import { Campaign } from "@/types/campaign";
import { toISODateString } from "@/utils/dates";
import { UTCDate } from "@date-fns/utc";
import { parseDate } from "@internationalized/date";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import { useCallback, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

export const Route = createFileRoute("/_dashboard/performance/")({
  component: RouteComponent,
  loader: async ({ context }) => {
    const campaignsQueryOptions = getCampaignsQueryOptions({
      includeStatistics: true,
    });
    const campaigns = await context.queryClient.ensureQueryData(
      campaignsQueryOptions,
    );
    return {
      useCampaignsQuery: () =>
        useQuery({
          ...campaignsQueryOptions,
          initialData: campaigns,
        }),
    };
  },
  staticData: {
    meta: {
      title: "Performance",
    },
  },
});

type DetailTableItem = {
  id: string;
  name: string;
  moves: number;
  leads: number;
  burnRate: string;
  periodAverageBurnRate: number;
  successRate: {
    value: number;
    percentage: number;
  };
};

type RangeValue<T> = {
  start: T;
  end: T;
};

function RouteComponent() {
  const { useCampaignsQuery } = Route.useLoaderData();
  const MotionInfo = motion.create(Info);
  const [selected, setSelected] = useState<Selection>(new Set(["campaign"]));
  const [selectedChart, setSelectedChart] = useState<Selection>(
    new Set(["handlinger"]),
  );
  const [showBurnRateTrend, setShowBurnRateTrend] = useState(false);

  const today = useMemo(() => {
    const date = new Date();
    return new UTCDate(date.getFullYear(), date.getMonth(), date.getDate());
  }, []);

  const currentMonthStart = useMemo(() => {
    return new UTCDate(today.getFullYear(), today.getMonth(), 1);
  }, [today]);

  const [dateRange, setDateRange] = useState<RangeValue<DateValue>>({
    start: parseDate(toISODateString(currentMonthStart)),
    end: parseDate(toISODateString(today)),
  });

  const { data: allCampaigns, isLoading: isLoadingCampaigns } =
    useCampaignsQuery();

  const campaigns = useMemo(
    () => allCampaigns.filter((campaign) => campaign.status !== "Inactive"),
    [allCampaigns],
  );

  const usersQuery = useInfiniteQuery({
    ...createUsersQueryOptions({
      nameFilter: "",
    }),
    throwOnError: false,
  });

  const responsibleMultiSelect = useMultiSelect({
    items: useMemo(
      () =>
        usersQuery?.data?.pages.flatMap((page) =>
          page.results.map((user) => ({
            key: user.id,
            label: user.name,
            value: user.id,
          })),
        ) ?? [],
      [usersQuery?.data?.pages],
    ),
  });

  const filteredCampaigns = useMemo(() => {
    if (!campaigns) return [];

    const selectedUserIds = responsibleMultiSelect.selectedValues.filter(
      (id) => id && id !== "all",
    );

    if (selectedUserIds.length === 0) {
      return campaigns;
    }

    return campaigns.filter(
      (campaign) =>
        campaign.responsibleId &&
        selectedUserIds.includes(campaign.responsibleId),
    );
  }, [campaigns, responsibleMultiSelect.selectedValues]);

  const campaignMultiSelect = useMultiSelect({
    items: useMemo(
      () =>
        filteredCampaigns
          ?.map((campaign) => ({
            key: campaign.id.toString(),
            label: campaign.name,
            value: campaign.id,
          }))
          .sort((a, b) => {
            return a.label?.localeCompare(b.label || "") || 0;
          }) ?? [],
      [filteredCampaigns],
    ),
  });

  const userNamesById = useMemo(
    () =>
      new Map(
        responsibleMultiSelect.items.map((user) => [user.key, user.label]),
      ),
    [responsibleMultiSelect],
  );

  const { campaignsById } = useMemo(() => {
    const campaignsById = new Map<
      number,
      {
        id: number;
        name: string;
        configurationNamesById: Map<number, string>;
        responsibleName: string;
      }
    >();

    for (const campaign of campaigns) {
      if (campaign.id === undefined) continue;

      const configurationNamesById = new Map<number, string>();
      for (const configuration of campaign.configurations) {
        if (
          configuration.id === undefined ||
          configuration.id === null ||
          !configuration.name
        )
          continue;
        configurationNamesById.set(configuration.id, configuration.name);
      }

      campaignsById.set(campaign.id, {
        id: campaign.id,
        name: campaign.name,
        configurationNamesById,
        responsibleName:
          (campaign.responsibleId
            ? userNamesById.get(campaign.responsibleId)
            : null) ?? "",
      });
    }

    return { campaignsById };
  }, [campaigns, userNamesById]);

  const apiParams: ActivityStatisticsParams = useMemo(() => {
    const userIds = responsibleMultiSelect.selectedValues
      .filter((id) => id && id !== "all")
      .map((id) => String(id));

    const campaignIds = campaignMultiSelect.selectedValues
      .filter((id) => id)
      .map((id) => Number(id))
      .filter((n) => !Number.isNaN(n));

    return {
      period: "week",
      from: dateRange.start.toString(),
      to: dateRange.end.toString(),
      userIds: userIds.length > 0 ? userIds : [],
      campaignIds: campaignIds.length > 0 ? campaignIds : [],
    };
  }, [
    dateRange,
    responsibleMultiSelect.selectedValues,
    campaignMultiSelect.selectedValues,
  ]);

  const {
    data: activitiesData,
    isUpdating,
    isFetching: isFetchingActivities,
    isWaitingForNewParams: isWaitingForNewActivitiesParams,
    hasStaleParams: hasStaleActivitiesParams,
  } = useActivitiesStatisticsWithRefresh(apiParams, 30000);

  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: "name",
    direction: "ascending",
  });

  const headerScrollRef = useRef<HTMLDivElement>(null);
  const bodyScrollRef = useRef<HTMLDivElement>(null);

  useHorizontalScrollSync(headerScrollRef, bodyScrollRef);

  const metrics = useMemo(() => {
    if (!activitiesData) {
      return {
        leadsReceived: 0,
        burnRate: { value: 0, percentage: 0 },
        successRate: { value: 0, percentage: 0 },
      };
    }

    return {
      leadsReceived: activitiesData.total.received,
      burnRate: {
        value: activitiesData.total.burned,
        percentage: Math.round(
          ((activitiesData.total.burned || 0) /
            (activitiesData.total.received || 1)) *
            100,
        ),
      },
      periodAverageBurnRate: parseFloat(
        activitiesData.total.averageBurnRate.toFixed(2),
      ),
      successRate: {
        value: activitiesData.total.success || 0,
        percentage: Math.round((activitiesData.total.successRate || 0) * 100),
      },
    };
  }, [activitiesData]);

  const userToCampaignsMap = useMemo(() => {
    const map = new Map<
      string,
      { campaignId: number; responsibleName: string }[]
    >();

    for (const campaign of campaigns || []) {
      if (campaign.id === undefined || !campaign.responsibleId) continue;

      const responsibleName = userNamesById.get(campaign.responsibleId) ?? "";

      if (!map.has(campaign.responsibleId)) {
        map.set(campaign.responsibleId, []);
      }

      map.get(campaign.responsibleId)?.push({
        campaignId: campaign.id,
        responsibleName: responsibleName,
      });
    }

    return map;
  }, [campaigns, userNamesById]);

  const items: DetailTableItem[] | undefined = useMemo(() => {
    return activitiesData?.campaigns.map((campaign) => {
      const singleCampaign = campaignsById.get(campaign.id);
      const burnRatePercentage = Math.round(
        ((campaign.burned || 0) / (campaign.received || 1)) * 100,
      );

      return {
        id: campaign.id.toString(),
        name: singleCampaign?.name ?? "-",
        moves: campaign.receivedAndHandled,
        leads: campaign.received,
        burnRate: `${campaign.burned} (${burnRatePercentage}%)`,
        periodAverageBurnRate: parseFloat(campaign.averageBurnRate.toFixed(2)),
        successRate: {
          value: Math.round(campaign.success),
          percentage: Math.round(campaign.successRate * 100),
        },
      };
    });
  }, [activitiesData?.campaigns]);

  const renderItems: DetailTableItem[] | undefined = useMemo(() => {
    if (Array.from(selected)[0] === "seller") {
      return activitiesData?.users.map((user) => {
        const userCampaigns = userToCampaignsMap.get(user.id) ?? [];

        const responsibleName =
          userCampaigns[0]?.responsibleName ??
          userNamesById.get(user.id) ??
          `User ${user.id}`;

        const burnRatePercentage = Math.round(
          ((user.burned || 0) / (user.received || 1)) * 100,
        );

        return {
          id: user.id.toString(),
          name: responsibleName,
          moves: user.receivedAndHandled,
          leads: user.received,
          burnRate: `${user.burned} (${burnRatePercentage}%)`,
          periodAverageBurnRate: parseFloat(user.averageBurnRate.toFixed(2)),
          successRate: {
            value: Math.round(user.success),
            percentage: Math.round(user.successRate * 100),
          },
        };
      });
    }
    return items;
  }, [selected, activitiesData?.users, items]);

  const graphCampaigns = useMemo(() => {
    if (!campaignMultiSelect.selectedValues.length) return allCampaigns;
    const idSet = new Set(campaignMultiSelect.selectedValues);
    return allCampaigns.filter((campaign) => idSet.has(campaign.id));
  }, [allCampaigns, campaignMultiSelect.selectedValues]);

  const debouncedGraphCampaigns = useDebounce(graphCampaigns, 800);

  const isWaitingForNewGraphCampaigns = useMemo(() => {
    const getJSON = (campaigns: Campaign[]) =>
      JSON.stringify(campaigns.map((c) => c.id).sort());
    return getJSON(graphCampaigns) !== getJSON(debouncedGraphCampaigns);
  }, [graphCampaigns, debouncedGraphCampaigns]);

  const typedTable = createTypedTable<DetailTableItem>();
  const { TableHeader: DetailsTableHeader, TableBody: DetailsTableBody } =
    typedTable;

  const detailTableColumns = typedTable.createColumns([
    { id: "name", name: "Navn", isRowHeader: true, width: 96 },
    { id: "leads", name: "Leads modtaget", width: 72, className: "text-left" },
    { id: "moves", name: "Handlinger", width: 72, className: "text-left" },
    {
      id: "burnRate",
      name: "Burn rate",
      width: 72,
      className: "text-left",
      renderCell: (row) => {
        return <span>{row.burnRate.toLocaleString()}</span>;
      },
    },
    {
      id: "periodAverageBurnRate",
      name: "Ugentlig burn rate",
      width: 72,
      className: "text-left",
      renderCell: (row) => <span>{row.periodAverageBurnRate}</span>,
    },
    {
      id: "successRate",
      name: "Succes rate",
      width: 72,
      className: "text-left",
      renderCell: (row) => (
        <span>
          {row.successRate.value.toLocaleString()}{" "}
          {`(${row.successRate.percentage || 0}%)`}
        </span>
      ),
    },
  ]);

  const chartHeaderRender = (
    type: "handlinger" | "modtaget",
  ): React.ReactNode => {
    switch (type) {
      case "handlinger":
        return (
          <div className="my-4 flex items-center gap-4">
            <div className="flex items-center gap-4">
              <span className="text-theme-on-glass text-lg font-medium">
                Aktivitet
              </span>
              <Separator className="h-full min-w-5 rotate-90" />
              <RAButton
                isDisabled={
                  !activitiesData?.periods.some((item) => item.burned > 0)
                }
                onPress={() => setShowBurnRateTrend(!showBurnRateTrend)}
                className={twMerge(
                  "flex items-center gap-2 rounded px-2 py-1 transition-opacity hover:bg-white/5",
                  !showBurnRateTrend && "opacity-50",
                )}
              >
                <ChartLine className="text-theme-on-glass" size={16} />
                <span className="text-xs font-medium text-nowrap">
                  Ugentlig burn rate
                </span>
              </RAButton>
              <Separator className="h-full min-w-5 rotate-90" />
              <div className="flex gap-x-5">
                <div className="flex items-center gap-2">
                  <StatusIndicator status="Handled" />
                  <span className="text-xs font-medium text-nowrap">
                    Succes: {activitiesData?.total?.success || 0}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="bg-theme-on-background-variant min-h-2 min-w-2 rounded-full" />
                  <span className="text-xs font-medium text-nowrap">
                    Diskvalificeret: {activitiesData?.total?.disqualified || 0}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="bg-theme-on-background-variant/50 min-h-2 min-w-2 rounded-full" />
                  <span className="text-xs font-medium text-nowrap">
                    Planlagt: {activitiesData?.total?.postponed || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        );
      case "modtaget": {
        const handled = activitiesData?.total?.receivedAndHandled ?? 0;
        const unhandled = activitiesData
          ? activitiesData.total.received - handled
          : 0;
        return (
          <div className="my-4 flex items-center gap-4">
            <div className="flex items-center gap-4">
              <span className="text-theme-on-glass text-lg font-medium text-nowrap">
                Aktivitet
              </span>
              <Separator className="h-full min-w-5 rotate-90" />
              <div className="flex gap-x-5">
                <div className="flex items-center gap-2">
                  <StatusIndicator status="Handled" />
                  <span className="text-xs font-medium text-nowrap">
                    Behandlet: {handled}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <StatusIndicator status="Draft" />
                  <span className="text-xs font-medium text-nowrap">
                    Ubehandlet: {unhandled}
                  </span>
                </div>
              </div>
            </div>
          </div>
        );
      }
    }
  };

  const selectedChartType = Array.from(selectedChart)[0] as
    | "handlinger"
    | "modtaget";

  const handleDateRangeChange = (range: RangeValue<DateValue> | null) => {
    setDateRange(range ?? dateRange);
  };

  const handleCampaignChange = useCallback(
    (keys: Selection) => {
      campaignMultiSelect.setSelectedKeys(keys);
    },
    [campaignMultiSelect],
  );

  const handleResponsibleChange = useCallback(
    (keys: Selection) => {
      responsibleMultiSelect.setSelectedKeys(keys);
      campaignMultiSelect.setSelectedKeys(new Set());
    },
    [responsibleMultiSelect, campaignMultiSelect],
  );

  const randomizedPeriods = useMemo(() => {
    const rand = (min: number, max: number) =>
      Math.floor(Math.random() * (max - min + 1)) + min;

    return (activitiesData?.periods ?? []).map((item) => {
      const received = rand(80, 220);
      const receivedAndHandled = rand(0, received);

      let left = receivedAndHandled;
      const success = rand(0, left);
      left -= success;
      const disqualified = rand(0, left);
      left -= disqualified;
      const postponed = left;

      const burned = rand(0, Math.max(0, received - receivedAndHandled));

      return {
        ...item,
        received,
        receivedAndHandled,
        success,
        disqualified,
        postponed,
        burned,
      } as ActivityStatisticsPeriod;
    });
  }, [activitiesData?.periods]);

  const utcMinDate = useMemo(
    () => new UTCDate(dateRange.start.toString()),
    [dateRange.start],
  );
  const utcMaxDate = useMemo(
    () => new UTCDate(dateRange.end.toString()),
    [dateRange.end],
  );

  return (
    <MainWindow>
      <PageHeader
        title="Performance"
        actions={
          <div className="flex items-center gap-2 xl:gap-4">
            <DateRangePicker
              maxValue={parseDate(new Date().toISOString().split("T")[0])}
              value={dateRange}
              onChange={handleDateRangeChange}
              aria-label="Period (from-to)"
            />

            <MultiSelect
              placeholder="Vælg sælger"
              buttonClassName="min-h-[54px] py-2 px-3"
              items={responsibleMultiSelect.items}
              selectedKeys={responsibleMultiSelect.selectedKeys}
              onSelectionChange={handleResponsibleChange}
            />

            <MultiSelect
              placeholder="Alle kampagner"
              buttonClassName="min-h-[54px] py-2 px-3"
              items={campaignMultiSelect.items}
              selectedKeys={campaignMultiSelect.selectedKeys}
              onSelectionChange={handleCampaignChange}
            />
          </div>
        }
      />

      <PageContent className="flex flex-col gap-8 pt-3 xl:pt-6">
        <div
          className={twMerge(
            "grid grid-cols-2 gap-3 px-8 transition-opacity duration-300 lg:grid-cols-4 xl:gap-6 xl:px-14",
            isUpdating && "opacity-50",
          )}
        >
          <Card
            className="border-theme-outline-variant w-full rounded-md border bg-transparent p-6"
            theme="none"
          >
            <div className="flex flex-col gap-6">
              <div className="flex items-center gap-4">
                <IconContainer>
                  <UserRoundCheck className="text-theme-gradient-from" />
                </IconContainer>
                <span className="font-medium text-nowrap text-ellipsis whitespace-nowrap">
                  Leads leveret
                </span>
              </div>
              <h2 className="text-theme-on-background text-[32px] leading-normal font-bold">
                {metrics?.leadsReceived?.toLocaleString()}
              </h2>
            </div>
          </Card>

          <Card
            className="border-theme-outline-variant w-full rounded-md border bg-transparent p-6"
            theme="none"
          >
            <div className="flex flex-col gap-6">
              <div className="flex items-center gap-4">
                <IconContainer>
                  <Flame className="text-theme-gradient-from" />
                </IconContainer>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-nowrap text-ellipsis whitespace-nowrap">
                    Samlet burn rate
                  </span>
                  <Tooltip
                    placement="right"
                    arrowPosition="right"
                    arrow
                    offset={20}
                    className="flex max-w-[300px] items-center self-start rounded-sm p-4"
                    tooltipText="Det samlede antal diskvalificerede eller succesfulde leads."
                  >
                    <Focusable className="pt-0.5">
                      <RAButton onClick={(e) => e.preventDefault()}>
                        <MotionInfo
                          size={16}
                          initial={{ color: "var(--theme-on-background)" }}
                          whileHover={{
                            color: "var(--theme-active)",
                            scale: 1.05,
                          }}
                          transition={{ type: "tween", duration: 0.2 }}
                          className={twMerge("cursor-pointer")}
                        />
                      </RAButton>
                    </Focusable>
                  </Tooltip>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <h2 className="text-theme-on-background text-[32px] leading-normal font-bold">
                  {metrics?.burnRate?.value?.toLocaleString()}
                </h2>
                <span className="text-theme-on-background-variant text-lg font-bold">
                  ({metrics.burnRate.percentage || 0}%)
                </span>
              </div>
            </div>
          </Card>

          <Card
            className="border-theme-outline-variant w-full rounded-md border bg-transparent p-6"
            theme="none"
          >
            <div className="flex flex-col gap-6">
              <div className="flex items-center gap-4">
                <IconContainer>
                  <Calendar1 className="text-theme-gradient-from" />
                </IconContainer>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-nowrap text-ellipsis whitespace-nowrap">
                    Ugentlig burn rate
                  </span>
                  <Tooltip
                    placement="right"
                    arrowPosition="right"
                    arrow
                    offset={20}
                    className="flex max-w-[300px] items-center self-start rounded-sm p-4"
                    tooltipText="Det gennemsnitlige antal diskvalificerede eller succesfulde leads, i uger med aktivitet."
                  >
                    <Focusable className="pt-0.5">
                      <RAButton onClick={(e) => e.preventDefault()}>
                        <MotionInfo
                          size={16}
                          initial={{ color: "var(--theme-on-background)" }}
                          whileHover={{
                            color: "var(--theme-active)",
                            scale: 1.05,
                          }}
                          transition={{ type: "tween", duration: 0.2 }}
                          className={twMerge("cursor-pointer")}
                        />
                      </RAButton>
                    </Focusable>
                  </Tooltip>
                </div>
              </div>
              <h2 className="text-theme-on-background text-[32px] leading-normal font-bold">
                {metrics.periodAverageBurnRate}
              </h2>
            </div>
          </Card>

          <Card
            className="border-theme-outline-variant w-full rounded-md border bg-transparent p-6"
            theme="none"
          >
            <div className="flex flex-col gap-6">
              <div className="flex items-center gap-4">
                <IconContainer>
                  <CheckCircle className="text-theme-gradient-from" />
                </IconContainer>
                <span className="font-medium text-nowrap text-ellipsis whitespace-nowrap">
                  Succes rate
                </span>
                <Tooltip
                  placement="left"
                  arrowPosition="left"
                  arrow
                  offset={20}
                  className="flex max-w-[300px] items-center self-start rounded-sm p-4"
                  tooltipText="Det samlede antal succesfulde leads. Procentsatsen udregnes imod succesfulde og diskvalificerede leads."
                >
                  <Focusable className="pt-0.5">
                    <RAButton onClick={(e) => e.preventDefault()}>
                      <MotionInfo
                        size={16}
                        initial={{ color: "var(--theme-on-background)" }}
                        whileHover={{
                          color: "var(--theme-active)",
                          scale: 1.05,
                        }}
                        transition={{ type: "tween", duration: 0.2 }}
                        className={twMerge("cursor-pointer")}
                      />
                    </RAButton>
                  </Focusable>
                </Tooltip>
              </div>
              <div className="flex items-center gap-2">
                <h2 className="text-theme-on-background text-[32px] leading-normal font-bold">
                  {metrics.successRate.value.toLocaleString()}
                </h2>
                <span className="text-theme-on-background-variant text-lg font-bold">
                  ({metrics.successRate.percentage}%)
                </span>
              </div>
            </div>
          </Card>
        </div>

        <Card
          className="relative w-full rounded-md bg-transparent"
          theme="none"
        >
          <div className="mb-3 flex w-full items-center justify-between px-8 xl:px-14">
            <div className="my-4 flex items-center gap-x-4">
              {chartHeaderRender(selectedChartType)}
            </div>
            <TagGroupBox
              defaultSelectedKeys={["handlinger"]}
              selectedKeys={selectedChart}
              onSelectionChange={setSelectedChart}
              selectionMode="single"
              selectionBehavior="replace"
            >
              <TagItem id="handlinger">Handlinger</TagItem>
              <TagItem id="modtaget">Leads modtaget</TagItem>
            </TagGroupBox>
          </div>

          <div
            className={twMerge(
              "transition-opacity duration-300",
              isUpdating && "opacity-50",
            )}
          >
            {selectedChartType === "handlinger" ? (
              <ActivitiesDashboardGraph
                periods={activitiesData?.periods ?? []}
                isLoading={
                  isLoadingCampaigns ||
                  (isFetchingActivities && hasStaleActivitiesParams) ||
                  isWaitingForNewActivitiesParams
                }
                alignment="left"
                baseDate={utcMinDate}
                minDate={utcMinDate}
                maxDate={utcMaxDate}
                showBurnRateTrend={showBurnRateTrend}
              />
            ) : (
              <CampaignLeadsGraph
                campaigns={debouncedGraphCampaigns}
                isLoadingCampaigns={
                  isLoadingCampaigns || isWaitingForNewGraphCampaigns
                }
                isSingleCampaign={false}
                noDataText="Ingen aktivitet i den viste periode"
                hideLegend={true}
                alignment="left"
                baseDate={utcMinDate}
                minDate={utcMinDate}
                maxDate={utcMaxDate}
              />
            )}
          </div>
        </Card>

        <div
          className={twMerge(
            "transition-opacity duration-300",
            isUpdating && "pointer-events-none opacity-50",
          )}
        >
          <typedTable.Table
            aria-label="Kampagner"
            className="max-w-full pb-4"
            columns={detailTableColumns}
            items={renderItems ?? []}
            sortDescriptor={sortDescriptor}
            setSortDescriptor={setSortDescriptor}
          >
            <StickyElement
              id="scroll-headers"
              ref={headerScrollRef}
              stickyProps={{ className: "bg-theme-background" }}
              className="overflow-x-scroll"
            >
              <div className="mb-3 flex w-full items-center justify-between px-8 xl:px-14">
                <div className="my-4 flex items-center gap-x-4">
                  <span className="text-theme-on-glass font-medium">
                    Detaljeret overblik
                  </span>
                </div>
                <TagGroupBox
                  defaultSelectedKeys={["campaign"]}
                  selectedKeys={selected}
                  onSelectionChange={setSelected}
                  selectionMode="single"
                  selectionBehavior="replace"
                >
                  <TagItem id="campaign">Kampagne</TagItem>
                  <TagItem id="seller">Sælger</TagItem>
                </TagGroupBox>
              </div>
              <DetailsTableHeader />
            </StickyElement>

            <DetailsTableBody
              id="scroll-headers"
              ref={bodyScrollRef}
              className="mb-5 min-w-0 overflow-x-scroll"
            />
          </typedTable.Table>
        </div>
      </PageContent>
    </MainWindow>
  );
}
